import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { Big } from 'big.js'
import { LayoutGroup } from 'framer-motion'
import { SubmitHandler, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { toast } from 'sonner'
import { z } from 'zod'

import { Currency } from '@/constants/currency'
import { queryKeys } from '@/constants/queryKeys'
import { SCOPE_OF_WORK_DESCRIPTION_PER_ROLE } from '@/constants/scope-of-work'
import { useErrorToast } from '@/hooks/useErrorToast'
import { queryClient } from '@/lib/queryClient'
import { getFullName } from '@/lib/typography'
import { cn } from '@/lib/utils'
import { AmountInput, Widget } from '@/shared/components'
import {
  AnimatedFormLabel,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  Input,
  MotionFormItem,
  Skeleton,
  SlideInLeft,
  StickyContainer,
  Textarea,
  Typography,
} from '@/shared/ui'

import { amendContract } from '../../api'
import {
  ContractorDetails,
  ContractorPaymentFrequency,
  ContractorPaymentType,
  ContractPaymentDayType,
} from '../../types'
import { CustomDaySelect } from '../CreateContractor/CustomDaySelect'
import { PaymentDaySelect } from '../CreateContractor/PaymentDaySelect'
import { PaymentFrequencySelect } from '../CreateContractor/PaymentFrequencySelect'
import { ScopeOfWorkField } from '../CreateContractor/ScopeOfWorkField'

const EDIT_CONTACT_DETAILS_FORM = `edit-contact-details-form-id`

const editContactDetailsSchema = z
  .object({
    contractorRole: z.string().min(1, {
      message: 'validation.role.required',
    }),
    scopeOfWorkRole: z.string().optional(),
    scopeOfWork: z.string().min(1, {
      message: 'validation.scopeOfWork.required',
    }),
    paymentType: z.string(),
    paymentDetails: z.object({
      amount: z.string().optional(),
      currency: z.nativeEnum(Currency).optional(),
      frequency: z.string().optional(),
      dayType: z.string().optional(),
      day: z.string().optional(),
    }),
  })
  .refine(
    (data) => {
      const { paymentDetails } = data

      if (
        paymentDetails.dayType === ContractPaymentDayType.CUSTOM_DAY_OF_WEEK ||
        paymentDetails.dayType === ContractPaymentDayType.CUSTOM_DAY_OF_MONTH ||
        paymentDetails.dayType ===
          ContractPaymentDayType.CUSTOM_DAY_OF_NEXT_MONTH
      ) {
        return paymentDetails.day !== ''
      }

      return true
    },
    { path: ['paymentDetails', 'day'] },
  )
  .refine(
    (data) => {
      const { paymentDetails, paymentType } = data

      if (paymentType === ContractorPaymentType.PAY_AS_YOU_GO) {
        return true
      }

      return paymentDetails.amount !== ''
    },
    {
      path: ['paymentDetails', 'amount'],
      message: 'validation.balance.minAmount',
    },
  )

type EditContactDetailsSchema = z.infer<typeof editContactDetailsSchema>

type Props = {
  contractor?: ContractorDetails
  setScreen: (screen: 'base') => void
}

export const EditContactDetailsScreen = ({ contractor, setScreen }: Props) => {
  const notifyError = useErrorToast()

  const latestContract = contractor?.contracts[0]

  const isFixedRateContract =
    latestContract?.paymentType === ContractorPaymentType.FIXED_RATE

  const form = useForm<EditContactDetailsSchema>({
    mode: 'onChange',
    resolver: zodResolver(editContactDetailsSchema),
    values: {
      contractorRole: latestContract?.contractorRole ?? '',
      scopeOfWork: latestContract?.scopeOfWork ?? '',
      scopeOfWorkRole: '',
      paymentType: latestContract?.paymentType ?? '',
      paymentDetails: {
        amount: latestContract?.paymentDetails?.amount
          ? Big(latestContract?.paymentDetails?.amount).toString()
          : '',
        currency: latestContract?.paymentDetails?.currency ?? Currency.USD,
        frequency:
          latestContract?.paymentDetails?.frequency ??
          ('' as ContractorPaymentFrequency),
        dayType:
          latestContract?.paymentDetails?.dayType ??
          ('' as ContractPaymentDayType),
        day: latestContract?.paymentDetails?.day
          ? latestContract?.paymentDetails?.day.toString()
          : '',
      },
    },
  })

  const { mutateAsync, isPending, isSuccess } = useMutation({
    mutationFn: amendContract,
  })

  const intl = useIntl()

  const onSubmit: SubmitHandler<EditContactDetailsSchema> = async (data) => {
    if (!latestContract?.id) {
      toast.error('Contract ID not found')

      return
    }

    const { scopeOfWorkRole, paymentType, ...contractDetails } = data

    try {
      await mutateAsync({
        contractId: latestContract.id,
        contractDetails: {
          ...contractDetails,
          paymentDetails:
            paymentType === ContractorPaymentType.PAY_AS_YOU_GO
              ? undefined
              : contractDetails.paymentDetails,
        },
      })

      await queryClient.refetchQueries({
        queryKey: [queryKeys.getContractor, contractor?.id],
      })

      toast.success(
        intl.formatMessage({
          id: 'contract.update.success',
          defaultMessage: 'Contract has been updated successfully',
        }),
      )

      setScreen('base')
    } catch (error) {
      notifyError(error)
    }
  }

  const customDayTypes = [
    ContractPaymentDayType.CUSTOM_DAY_OF_WEEK,
    ContractPaymentDayType.CUSTOM_DAY_OF_MONTH,
    ContractPaymentDayType.CUSTOM_DAY_OF_NEXT_MONTH,
  ]

  return (
    <SlideInLeft className="flex h-full flex-col">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbLink asChild>
            <Button
              onClick={() => setScreen('base')}
              variant="ghost"
              size="inline"
            >
              {latestContract ? (
                <Typography>
                  <FormattedMessage
                    id="contractors.contract.type.label"
                    defaultMessage="{state, select, FIXED_RATE {Fixed-rate contract} PAY_AS_YOU_GO {Pay-as-you-go contract} other {}}"
                    values={{ state: latestContract?.paymentType }}
                  />
                </Typography>
              ) : (
                <Skeleton className="h-6 w-36" />
              )}
            </Button>
          </BreadcrumbLink>

          <BreadcrumbSeparator />

          <BreadcrumbItem>
            <Typography>
              <FormattedMessage
                id="contract.details.edit.title"
                defaultMessage="Edit details"
              />
            </Typography>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="p-4" />

      <Typography variant="h3">
        <FormattedMessage
          id="contractor.details.edit.title"
          defaultMessage="Edit contact details"
        />
      </Typography>

      <div className="p-2" />

      <Typography>
        <FormattedMessage
          id="contractor.details.edit.description"
          defaultMessage="Edit {fullName} contract"
          values={{ fullName: getFullName(contractor?.personalDetails) }}
        />
      </Typography>

      <div className="p-4" />

      <Form {...form}>
        <form
          id={EDIT_CONTACT_DETAILS_FORM}
          className="h-full"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-8">
            <Widget
              title={
                <FormattedMessage
                  defaultMessage="Job details"
                  id="label.jobDetails"
                />
              }
              variant="form"
            >
              <FormField
                control={form.control}
                name="contractorRole"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder={intl.formatMessage({
                          defaultMessage: 'Role',
                          id: 'label.role',
                        })}
                        {...field}
                      />
                    </FormControl>
                    <AnimatedFormLabel>
                      <FormattedMessage defaultMessage="Role" id="label.role" />
                    </AnimatedFormLabel>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="scopeOfWorkRole"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ScopeOfWorkField
                        variant="sidebar"
                        value={field.value}
                        onSelect={(value) => {
                          field.onChange(value)

                          if (SCOPE_OF_WORK_DESCRIPTION_PER_ROLE[value]) {
                            const scopeOfWork = (
                              SCOPE_OF_WORK_DESCRIPTION_PER_ROLE[value]
                                .defaultMessage ?? ''
                            ).toString()

                            form.setValue('scopeOfWork', scopeOfWork)
                          }
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="scopeOfWork"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea
                        className="relative"
                        placeholder={intl.formatMessage({
                          defaultMessage: 'Scope of work',
                          id: 'label.scopeOfWork',
                        })}
                        {...field}
                      />
                    </FormControl>
                    <AnimatedFormLabel size="textarea">
                      <FormattedMessage
                        defaultMessage="Scope of work"
                        id="label.scopeOfWork"
                      />
                    </AnimatedFormLabel>
                  </FormItem>
                )}
              />
            </Widget>

            {isFixedRateContract && (
              <Widget
                title={
                  <FormattedMessage
                    defaultMessage="Payment details"
                    id="label.paymentDetails"
                  />
                }
                variant="form"
              >
                <FormField
                  control={form.control}
                  name="paymentDetails.amount"
                  render={({ field }) => (
                    <FormItem>
                      <AmountInput
                        placeholder={intl.formatMessage({
                          defaultMessage: 'Payment amount',
                          id: 'label.paymentAmount',
                        })}
                        {...field}
                      />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="paymentDetails.frequency"
                  render={({ field }) => (
                    <FormItem>
                      <PaymentFrequencySelect
                        value={field.value}
                        onChange={(v) => {
                          field.onChange(v)

                          form.setValue('paymentDetails.dayType', '')
                          form.setValue('paymentDetails.day', '')
                        }}
                      />
                    </FormItem>
                  )}
                />

                <LayoutGroup>
                  <FormField
                    control={form.control}
                    name="paymentDetails.dayType"
                    render={({ field }) => (
                      <MotionFormItem layout>
                        <PaymentDaySelect
                          frequency={
                            form.watch(
                              'paymentDetails.frequency',
                            ) as ContractorPaymentFrequency
                          }
                          value={field.value}
                          onChange={(v) => {
                            field.onChange(v)

                            form.setValue('paymentDetails.day', '')
                          }}
                        />
                      </MotionFormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="paymentDetails.day"
                    render={({ field }) =>
                      customDayTypes.includes(
                        (form.watch('paymentDetails.dayType') ??
                          '') as ContractPaymentDayType,
                      ) ? (
                        <MotionFormItem layout>
                          <CustomDaySelect
                            paymentDayType={
                              form.watch(
                                'paymentDetails.dayType',
                              ) as ContractPaymentDayType
                            }
                            value={field.value}
                            onChange={field.onChange}
                          />
                        </MotionFormItem>
                      ) : (
                        <></>
                      )
                    }
                  />
                </LayoutGroup>
              </Widget>
            )}
          </div>
        </form>
      </Form>

      <div className="p-8" />

      <StickyContainer
        className={cn('-bottom-16', isFixedRateContract ? '' : 'pb-0')}
      >
        <Button
          width="full"
          form={EDIT_CONTACT_DETAILS_FORM}
          onClick={form.handleSubmit(onSubmit)}
          loading={isPending || isSuccess}
          disabled={
            !form.formState.isValid ||
            !form.formState.isDirty ||
            isPending ||
            isSuccess
          }
          type="submit"
        >
          <FormattedMessage
            defaultMessage="Save changes"
            id="action.saveChanges"
          />
        </Button>
      </StickyContainer>
    </SlideInLeft>
  )
}
